import { FC, useEffect, useMemo, useState } from 'react'
import { CollapseIcon, ProfileIcon } from './'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Children, sideBarMenuItems } from '../helpers/sideBarRoutes'
import { carrierStore } from '../store/carrier'
import {
	Badge,
	Button,
	Flex,
	Input,
	Modal,
	Spin,
	Tooltip
} from 'antd'
import { GlobalOutlined } from '@ant-design/icons'
import {
	RNCommon,
	RNLogging,
	RNProfile,
	RNWelcome
} from '../helpers/routeNames'
import NotificationModal from './NotificationModal'
import { notificationStore } from '../store/notification'
import i18next from 'i18next'
import { authStore } from '../store/auth'
import { sensorStore } from '../store/sensor'

export const SideBar: FC = () => {
	const location = useLocation()
	const { t } = useTranslation()

	const { carriers } = carrierStore()
	const { user } = authStore()

	const sideBarCarriers = useMemo(
		() =>
			carriers.map((carrier) => ({
				path: `/carriers/${carrier.id}`,
				name: carrier.name,
				activeProblemsCount: carrier.activeProblemsCount,
				carrierId: carrier.id
			})),
		[carriers]
	)

	const [sideBarMenu, setSideBarMenu] = useState(
		sideBarMenuItems(sideBarCarriers)
	)

	const [searchEls, setSearchEls] = useState<Children[]>([])
	const [searchValue, setSearchValue] = useState('')
	const {
		isOpen: isNotificationModalOpen,
		setIsOpen: setIsNotificationModalOpen
	} = notificationStore()
	const [isLangModalOpen, setIsLangModalOpen] = useState(false)

	const { isFetched: isSensorsFetched, sensors } = sensorStore()

	const onSearch = (value: string) => {
		setSearchValue(value)
		if (value === '') {
			setSearchEls([])
			return
		}
		const children = sideBarMenu.find((el) =>
			el.path.includes('/carriers')
		)?.children

		if (!children) return
		setSearchEls(
			children.filter((el) =>
				el.name.toLowerCase().includes(value.toLowerCase())
			)
		)
	}

	useEffect(() => {
		setSideBarMenu(sideBarMenuItems(sideBarCarriers))
	}, [sideBarCarriers])

	if (!user) return <></>

	return location.pathname.startsWith(RNCommon) ? null : (
		<div className="sideBar">
			<Link to={RNWelcome} className="sideBar-logo">
				<img src="/logo.png" alt="Logo" width={30} />
				<div>
					<p className="sideBar-logo-name">O'zbekiston</p>
					<p className="sideBar-logo-country">
						Temir Yo'llari
					</p>
				</div>
			</Link>
			<Flex
				align="center"
				justify="space-evenly"
				className="search"
				style={{
					backgroundColor: '#2D4056',
					padding: '5px 10px'
				}}>
				<div className="search-notification">
					<GlobalOutlined
						style={{ fontSize: 30, color: '#BCC6D8' }}
						onClick={() => setIsLangModalOpen(true)}
					/>
					<Modal
						open={isLangModalOpen}
						footer={null}
						onCancel={() => setIsLangModalOpen(false)}>
						<Flex vertical gap={16}>
							<Flex vertical gap={8}>
								<Button
									type={
										i18next.language === 'ru'
											? 'primary'
											: 'default'
									}
									onClick={() => {
										i18next.changeLanguage('ru')
										setIsLangModalOpen(false)
									}}>
									Русский
								</Button>
								<Button
									type={
										i18next.language === 'uz'
											? 'primary'
											: 'default'
									}
									onClick={() => {
										i18next.changeLanguage('uz')
										setIsLangModalOpen(false)
									}}>
									O'zbek
								</Button>
								<Button
									type={
										i18next.language === 'kz'
											? 'primary'
											: 'default'
									}
									onClick={() => {
										i18next.changeLanguage('kz')
										setIsLangModalOpen(false)
									}}>
									Қазақ
								</Button>
							</Flex>
						</Flex>
					</Modal>
				</div>
				<div className="search-notification">
					<NotificationModal
						isOpen={isNotificationModalOpen}
						setIsOpen={setIsNotificationModalOpen}
					/>
				</div>
				<Link to={RNProfile} className="search-profile">
					<ProfileIcon
						isActive={location.pathname === RNProfile}
					/>
				</Link>
			</Flex>
			<ul className="sideBar-menu">
				<li
					className="sideBar-menu-item"
					style={{
						zIndex: 2,
						backgroundColor: '#2D4056'
					}}>
					<Input.Search
						type="search"
						placeholder={t('Search') as string}
						autoFocus
						onChange={(e) => {
							if (!e.target.value.length) {
								setSearchEls([])
								setSearchValue('')
							}
						}}
						onSearch={(e) => onSearch(e)}
						size="large"
						style={{
							margin: 10,
							width: 'calc(100% - 20px)'
						}}
					/>
				</li>

				{!searchValue.length ? (
					(user.email.includes('@zarafshon.org')
						? sideBarMenu
						: sideBarMenu.filter((a) => !a.isAdmin)
					).map(
						({
							path,
							name,
							Icon,
							last = false,
							children = [],
							isOpen = location.pathname.includes(path)
						}) => (
							<li
								className={`sideBar-menu-item${
									location.pathname.includes(path)
										? ' selected'
										: ''
								}${last ? ' sidebar-last' : ''}${
									children.length ? ' hasSub' : ''
								}`}
								style={{
									minHeight: 66,
									maxHeight: !isOpen
										? 66
										: 'calc(100vh - 110px)'
								}}
								key={name}>
								<Link
									to={children.length ? '#' : path}
									className="sideBar-menu-item-link"
									onClick={
										children.length
											? (e) => {
													e.preventDefault()
													setSideBarMenu(
														sideBarMenu.map(
															(item) => {
																if (
																	item.name ===
																	name
																) {
																	item.isOpen =
																		!item.isOpen
																}
																return item
															}
														)
													)
												}
											: () => {}
									}>
									<Icon
										isActive={location.pathname.includes(
											path
										)}
									/>
									<span className="sideBar-menu-item-text">
										{t(name)}
									</span>
									{children.length ? (
										<CollapseIcon
											collapsed={isOpen}
										/>
									) : (
										''
									)}
								</Link>
								{children.length ? (
									<ul
										className={`sideBar-menu sub${
											isOpen ? ' open' : ''
										}`}>
										{(user.email.includes(
											'@zarafshon.org'
										)
											? children
											: children.filter(
													(a) => !a.isAdmin
												)
										).map(
											({
												path,
												name,
												activeProblemsCount
											}) => {
												const carrierId = path
													.split('/')
													.pop()
												return (
													<li
														className={`sideBar-menu-item child${
															location.pathname.includes(
																path
															)
																? ' selected'
																: ''
														}`}
														key={name}
														style={{
															display:
																'flex',
															alignItems:
																'center',
															justifyContent:
																'space-between'
														}}>
														<Link
															to={path}
															className="sideBar-menu-item-link"
															style={{
																pointerEvents:
																	!isSensorsFetched
																		? 'none'
																		: 'auto',
																flex: 1
															}}>
															<span className="sideBar-menu-item-text">
																{t(name)}
																{!isSensorsFetched && (
																	<Spin size="large" />
																)}
															</span>
														</Link>
														{(activeProblemsCount ??
															0) >
															0 && (
															<Link
																to={`${RNLogging}/${carrierId}?onlyErrors=true`}
																style={{
																	padding:
																		'0 8px',
																	display:
																		'inline-flex',
																	alignItems:
																		'center'
																}}
																onClick={(
																	e
																) =>
																	e.stopPropagation()
																}>
																<Badge
																	count={
																		activeProblemsCount
																	}
																	size="small"
																	style={{
																		backgroundColor:
																			'#ff4d4f'
																	}}
																/>
															</Link>
														)}
														{sensors.some(
															(s) =>
																s.code ===
																	'side_netw_pult' &&
																s
																	.sensorData[0]
																	?.value ===
																	'1' &&
																s
																	.sensorData[0]
																	?.carrierId ===
																	Number(
																		carrierId
																	)
														) && (
															<Tooltip
																title={t(
																	'NetworkPanelDisconnected'
																)}
																placement="right">
																<Badge
																	status="warning"
																	size="small"
																/>
															</Tooltip>
														)}
													</li>
												)
											}
										)}
									</ul>
								) : (
									''
								)}
							</li>
						)
					)
				) : searchEls.length && searchValue.length ? (
					searchEls.map(({ path, name }) => (
						<li
							className={`sideBar-menu-item${
								location.pathname.includes(path)
									? ' selected'
									: ''
							}`}
							key={name}>
							<Link
								to={path}
								className="sideBar-menu-item-link">
								<span className="sideBar-menu-item-text">
									{t(name)}
								</span>
							</Link>
						</li>
					))
				) : !searchEls.length && searchValue.length ? (
					<li
						className={`sideBar-menu-item`}
						key={t('NoResults')}>
						<Link
							to={'#'}
							onClick={() => setSearchValue('')}
							className="sideBar-menu-item-link">
							<b className="sideBar-menu-item-text">
								{t('NoResults')}
							</b>
						</Link>
					</li>
				) : (
					''
				)}
			</ul>
		</div>
	)
}
